import { useTranslation } from 'react-i18next';
import { Autocomplete, Grid, TextField } from '@mui/material';
import { isEmpty } from 'lodash/lang';
import { get } from 'lodash/object';

import useGeo from '../../../../../../../services/geo/useGeo';
import { NSC_ADDRESS } from '../../../../../../../services/i18n/namespaces';
import { normalizePostalCode } from '../../../../../../../services/normalizers';

import Address1Autocomplete from './Address1Autocomplete/Address1Autocomplete';
import CityAutocomplete from './CityAutocomplete/CityAutocomplete';
import AddressValidator from './AddressValidator';

export default function AddressFields({
  path,
  values,
  errors,
  setAddressField,
  setCountryId,
  setCity,
  readOnly = false
}) {
  const { t } = useTranslation();
  const data = get(values, path);

  const countryId = data.countryId;
  const regionId = data.regionId;
  const { countries, regions, matchedCountry, matchedRegion } = useGeo(countryId, regionId);

  return (
    <>
      <Grid size={4}>
        <Autocomplete
          disabled={readOnly}
          value={matchedCountry}
          onChange={function({ target }, country) {
            setCountryId(path, country?.id || '');
          }}
          onBlur={function(e) {
            AddressValidator.revalidate(path);
          }}
          fullWidth
          getOptionKey={option => option.id}
          getOptionLabel={option => option.name ?? option}
          disablePortal
          options={countries}
          renderInput={params => (
            <TextField
              {...params}
              name={`${path}.countryId`}
              label={t(`${NSC_ADDRESS}:country`)}
              variant="standard"
              required
              error={getErrorBoolean('countryId')}
              helperText={getHelperText('countryId')}
              autoComplete="off"
            />
          )}
        />
      </Grid>
      <Grid size={4}>
        <Autocomplete
          disabled={readOnly || isEmpty(regions)}
          value={matchedRegion}
          onChange={function({ target }, region) {
            setAddressField(`${path}.regionId`, region?.id || '');
          }}
          onBlur={function(e) {
            AddressValidator.revalidate(path);
          }}
          fullWidth
          getOptionKey={option => option.id}
          getOptionLabel={regionOptionLabel}
          disablePortal
          options={regions}
          renderInput={params => (
            <TextField
              {...params}
              name={`${path}.regionId`}
              label={t(`${NSC_ADDRESS}:${data.countryId === 'CA' ? 'province' : 'state'}`)}
              variant="standard"
              required
              error={getErrorBoolean('regionId')}
              helperText={
                getHelperText('regionId')
                  ? t(`${NSC_ADDRESS}:${data.countryId === 'CA' ? 'province' : 'state'}`) + getHelperText('regionId')
                  : null
              }
              autoComplete="off"
            />
          )}
        />
      </Grid>
      <Grid size={4}>
        <CityAutocomplete
          readOnly={readOnly}
          name={`${path}.city`}
          matchedCountry={matchedCountry}
          matchedRegion={matchedRegion}
          city={data.city}
          onChange={function(value) {
            setCity(path, value);
          }}
          onBlur={function(e) {
            AddressValidator.revalidate(path);
          }}
          error={getErrorBoolean('city')}
          helperText={getHelperText('city')}
        />
      </Grid>
      <Grid size={6}>
        <Address1Autocomplete
          readOnly={readOnly}
          name={`${path}.address1`}
          matchedCountry={matchedCountry}
          matchedRegion={matchedRegion}
          city={data.city}
          address1={data.address1}
          valid={data.valid}
          onChange={function(value) {
            setAddressField(`${path}.address1`, value || '');
          }}
          onBlur={function(e) {
            AddressValidator.revalidate(path);
          }}
          error={getErrorBoolean('address1')}
          helperText={getHelperText('address1')}
        />
      </Grid>
      <Grid size={3}>
        <TextField
          disabled={readOnly}
          name={`${path}.address2`}
          value={data.address2 || ''}
          onChange={function({ target }) {
            setAddressField(target.name, target.value || '');
          }}
          error={getErrorBoolean('address2')}
          helperText={getHelperText('address2')}
          fullWidth
          label={t(`${NSC_ADDRESS}:address2`)}
          variant="standard"
          autoComplete="off"
        />
      </Grid>
      <Grid size={3}>
        <TextField
          disabled={readOnly}
          name={`${path}.zipCode`}
          value={data.zipCode || ''}
          onChange={function({ target }) {
            setAddressField(target.name, normalizePostalCode(target.value, data.countryId));
          }}
          onBlur={function(e) {
            AddressValidator.revalidate(path);
          }}
          error={getErrorBoolean('zipCode')}
          helperText={getHelperText('zipCode')}
          fullWidth
          label={t(`${NSC_ADDRESS}:zip`)}
          variant="standard"
          required
          autoComplete="off"
        />
      </Grid>
    </>
  );
  function getErrorBoolean(fieldName) {
    return Boolean(get(errors, `${path}.${fieldName}`));
  }
  function getHelperText(fieldName) {
    return get(errors, `${path}.${fieldName}`);
  }
}

function regionOptionLabel(option) {
  let label = '';
  if (option.id) {
    label += option.id;
  }
  if (option.localName || option.name) {
    label += ` (${option.localName || option.name})`;
  }
  return label || option;
}
